import { useMutation } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { getAccessToken } from "../services/authStore";
import { useApiQuery, useApiMutation } from "../services/react-query.js";

export const useGetCompliancePolicy = ({ eventId }) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["compliancePolicy", eventId],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/events/${eventId}/compliance/policy`,
      method: "GET",
    },
  });

export const useUpdateCompliancePolicy = ({ eventId }) =>
  useApiMutation({
    mutationOptions: { mutationKey: ["updateCompliancePolicy", eventId] },
    fetchOptions: {
      url: `/events/${eventId}/compliance/policy`,
      method: "PUT",
    },
    invalidateQueryKeys: [["compliancePolicy", eventId]],
    successMessage: { title: "Compliance text saved" },
  });

export const useGetComplianceActivity = ({
  eventId,
  activity,
  search,
  page,
  limit,
}) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["complianceActivity", eventId, activity, search, page, limit],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/events/${eventId}/compliance/activity`,
      method: "GET",
      // Normalised to null, which the query-string builder strips. An
      // `undefined` would be serialised as the literal "undefined" and fail the
      // endpoint's activity enum.
      params: {
        activity: activity ?? null,
        search: search || null,
        page,
        limit,
      },
    },
  });

export const useGetComplianceInsights = ({ eventId, enabled = true }) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["complianceInsights", eventId],
      enabled: !!eventId && enabled,
    },
    fetchOptions: {
      url: `/events/${eventId}/insights/compliance`,
      method: "GET",
    },
  });

export const useExportComplianceActivity = () =>
  useMutation({
    mutationKey: ["exportComplianceActivity"],
    mutationFn: async ({ eventId, activity, search }) => {
      const token = await getAccessToken();
      const query = new URLSearchParams();
      if (activity) query.set("activity", activity);
      if (search) query.set("search", search);
      const queryString = query.toString() ? `?${query.toString()}` : "";

      const response = await fetch(
        `/api/events/${eventId}/compliance/activity/export${queryString}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/csv",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to export the compliance activity log");
      }

      return response.blob().then((blob) => ({ blob, eventId }));
    },
    onSuccess: ({ blob, eventId }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `congrs-compliance-activity-${eventId}-${new Date().toDateString()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      notifications.show({
        title: "Compliance activity log exported successfully",
        color: "green",
      });
    },
    onError: () => {
      notifications.show({
        title: "Failed to export the compliance activity log",
        color: "red",
      });
    },
  });
